import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { AppDispatch, RootState } from '@/store';
import { updateProvider } from '@/features/provider/providerSlice';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { useToast } from '@/components/ui/use-toast';
import { Loader2, Upload, CheckCircle, XCircle, Building, Globe, Mail, Phone, Link2, X, UploadIcon } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { tunisianGovernorates, governorateCities } from '@/data/tunisian-governorates';
import categoryNames from "@/data/categoryNames.json";
import { Provider } from '@/features/provider/types';
import { getCookie } from '@/utils/cookies';
import apolloClient from '@/lib/apollo-client';
import { GET_PROVIDER_BY_ID } from '@/lib/graphql/queries/providerQueries';
import ProviderLogo from '@/components/common/ProviderLogo';
import ProviderImage from '@/components/common/ProviderImage';

const EditProviderForm = () => {
  const { id } = useParams<{ id: string }>();
  console.log("Provider ID from URL params:", id); // Updated log message for clarity
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { loading: storeLoading, error: storeError } = useSelector((state: RootState) => state.provider);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Update the formData initialization to not include id in the form object
  const [formData, setFormData] = useState({
    name: '',
    city: '',
    country: 'Tunisia',
    state: '',
    postalCode: '',
    address: '',
    description: '',
    yearFounded: '',
    type: 'university' as const,
    website: '',
    email: '',
    phone: '',
    linkedin: '',
    x: '',
    ownership: 'private' as const,
    category: '',
    languages: ['English'],
    degreeTypes: [] as string[]
  });

  const [logo, setLogo] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [currentTab, setCurrentTab] = useState('basic');
  const [selectedGovernorate, setSelectedGovernorate] = useState<string>('');
  const [originalProvider, setOriginalProvider] = useState<Provider | null>(null);
  
  // Add state for multiple images
  const [images, setImages] = useState<File[]>([]);
  const [imagesPreviews, setImagesPreviews] = useState<string[]>([]);
  const [existingImages, setExistingImages] = useState<string[]>([]);
  const [imagesToRemove, setImagesToRemove] = useState<string[]>([]);

  // Fetch provider data
  useEffect(() => {
    const fetchProvider = async () => {
      if (!id) return;
      
      setLoading(true);
      setError(null);
      
      try {
        const token = getCookie('token');
        const providerId = parseInt(id, 10);
        
        if (isNaN(providerId)) {
          throw new Error('Invalid provider ID');
        }
        
        console.log("Fetching provider with ID:", providerId); // Add this log to track the ID being used
        
        const { data } = await apolloClient.query({
          query: GET_PROVIDER_BY_ID,
          variables: { id: providerId },
          context: {
            headers: {
              Authorization: `Bearer ${token}`
            }
          },
          fetchPolicy: 'network-only' // Don't use cache for this query
        });
        
        if (!data || !data.provider) {
          throw new Error('Provider not found');
        }
        
        console.log(JSON.stringify(data)+"eddddddit")
        // Handle potential field name differences between API and interface
        const providerData = {
          ...data.provider,
          // Ensure degreeTypes is always an array
          degreeTypes: data.provider.degreeTypes || 
                      (data.provider.degreeType ? [data.provider.degreeType] : [])
        };
        
        setOriginalProvider(providerData);
        
        // Populate form data
        setFormData({
          name: providerData.name || '',
          city: providerData.city || '',
          country: providerData.country || 'Tunisia',
          state: providerData.state || '',
          postalCode: providerData.postalCode || '',
          address: providerData.address || '',
          description: providerData.description || '',
          yearFounded: providerData.yearFounded ? String(providerData.yearFounded) : '',
          type: providerData.type || 'university',
          website: providerData.website || '',
          email: providerData.email || '',
          phone: providerData.phone || '',
          linkedin: providerData.linkedin || '',
          x: providerData.x || '',
          ownership: providerData.ownership || 'private',
          category: providerData.category || '',
          languages: providerData.languages || ['English'],
          degreeTypes: providerData.degreeTypes || []
        });
        
        // Set logo preview if available
        if (providerData.logoUrl) {
          setLogoPreview(providerData.logoUrl);
        }
        
        // Set existing images if available
        if (providerData.imageUrls && providerData.imageUrls.length > 0) {
          setExistingImages(providerData.imageUrls);
        }
        
        // Set selected governorate
        if (providerData.state) {
          setSelectedGovernorate(providerData.state);
        }
      } catch (err) {
        console.error('Error fetching provider:', err);
        setError(err instanceof Error ? err.message : 'Failed to load provider details');
        toast({
          title: "Error",
          description: "Failed to load provider details. Please try again.",
          variant: "destructive",
        });
      } finally {
        setLoading(false);
      }
    };
    
    fetchProvider();
  }, [id, toast]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name: string, value: string) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleGovernorateChange = (value: string) => {
    setSelectedGovernorate(value);
    // Optionally reset city when governorate changes
    if (formData.country === 'Tunisia') {
      setFormData(prev => ({ ...prev, state: value, city: '' }));
    }
  };

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const selectedFile = e.target.files[0];
      setLogo(selectedFile);
      
      // Create preview URL
      const reader = new FileReader();
      reader.onload = (e) => {
        setLogoPreview(e.target?.result as string);
      };
      reader.readAsDataURL(selectedFile);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const droppedFile = e.dataTransfer.files[0];
      if (droppedFile.type.startsWith('image/')) {
        setLogo(droppedFile);
        
        // Create preview URL
        const reader = new FileReader();
        reader.onload = (e) => {
          setLogoPreview(e.target?.result as string);
        };
        reader.readAsDataURL(droppedFile);
      } else {
        toast({
          title: 'Invalid File Type',
          description: 'Please select an image file',
          variant: 'destructive',
        });
      }
    }
  };

  // Add these handlers after your existing handlers (around line 222)
  
  // Add handler for multiple images
  const handleImagesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const selectedFiles = Array.from(e.target.files);
      setImages(prev => [...prev, ...selectedFiles]);
      
      // Create previews for each selected file
      selectedFiles.forEach(file => {
        const reader = new FileReader();
        reader.onloadend = () => {
          if (reader.result) {
            setImagesPreviews(prev => [...prev, reader.result as string]);
          }
        };
        reader.readAsDataURL(file);
      });
    }
  };

  const removeImage = (index: number) => {
    setImages(prev => prev.filter((_, i) => i !== index));
    setImagesPreviews(prev => prev.filter((_, i) => i !== index));
  };
  
  // Add handler to mark an existing image for removal
  const removeExistingImage = (url: string) => {
    setImagesToRemove(prev => [...prev, url]);
    setExistingImages(prev => prev.filter(img => img !== url));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!id) {
      toast({
        title: 'Error',
        description: 'Provider ID is missing',
        variant: 'destructive',
      });
      return;
    }
    
    // Ensure ID is properly parsed as a number
    const providerId = parseInt(id, 10);
    
    if (isNaN(providerId)) {
      toast({
        title: 'Error',
        description: 'Invalid provider ID format',
        variant: 'destructive',
      });
      return;
    }
    
    console.log("Submitting update for provider ID:", providerId); // Add this log
    
    // Convert yearFounded to number if provided
    const input = {
      ...formData,
      id: providerId, // Use the parsed ID
      yearFounded: formData.yearFounded ? parseInt(formData.yearFounded) : undefined,
      // Add the list of images to remove
      removeImages: imagesToRemove.length > 0 ? imagesToRemove : undefined,
      // Include existing images that should be kept
      existingImages: existingImages.length > 0 ? existingImages : undefined
    };
    
    try {
      // Validate required fields on the client side
      const requiredFields = ['name', 'email', 'country', 'city', 'type', 'ownership', 'address'];
      const missingFields = requiredFields.filter(field => !formData[field]);
      
      if (missingFields.length > 0) {
        toast({
          title: 'Validation Error',
          description: `Please fill in all required fields: ${missingFields.join(', ')}`,
          variant: 'destructive',
        });
        return;
      }
      
      // Validate name length
      if (formData.name.length > 100) {
        toast({
          title: 'Validation Error',
          description: 'Provider name must be less than 100 characters',
          variant: 'destructive',
        });
        return;
      }
      
      // Validate URL fields
      const urlFields = ['website', 'linkedin', 'x'];
      const urlPattern = /^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([/\w .-]*)*\/?$/;
      const invalidUrls = urlFields
        .filter(field => formData[field] && !urlPattern.test(formData[field]))
        .map(field => field);
      
      if (invalidUrls.length > 0) {
        toast({
          title: 'Validation Error',
          description: `Please enter valid URLs for: ${invalidUrls.join(', ')}`,
          variant: 'destructive',
        });
        return;
      }
      
      // Format URLs to ensure they have http:// prefix if missing
      const formattedInput = {
        ...input,
      };
      
      urlFields.forEach(field => {
        if (formData[field] && !formData[field].startsWith('http')) {
          formattedInput[field] = `https://${formData[field]}`;
        }
      });
      
      // Use the updateProvider action with the correct order of fields for multipart upload
      await dispatch(updateProvider({ 
        input: formattedInput, 
        logo, 
        images 
      })).unwrap();
      
      toast({
        title: 'Success',
        description: 'Provider updated successfully',
        variant: 'default',
      });
     // navigate('/admin/providers');
    } catch (err: any) {
      // Check if the error contains formatted validation errors
      if (err?.formattedErrors) {
        const errorMessages = Object.entries(err.formattedErrors)
          .map(([field, message]) => `${field}: ${message}`)
          .join('\n');
        
        toast({
          title: 'Validation Error',
          description: errorMessages,
          variant: 'destructive',
        });
      } else {
        const errorMessage = err?.message || error || 'Failed to update provider';
        toast({
          title: 'Error',
          description: errorMessage,
          variant: 'destructive',
        });
      }
      
      console.error('Provider update error:', err);
    }
  };


  const removeLogo = () => {
    setLogo(null);
    // If we're editing and there was an original logo, we need to keep track that we want to remove it
    if (originalProvider?.logoUrl) {
      // Set a placeholder to indicate we want to remove the logo
      setLogoPreview(null);
    } else {
      setLogoPreview(null);
    }
  };
  


  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <span className="ml-2">Loading provider data...</span>
      </div>
    );
  }

  if (error) {
    return (
      <Card className="w-full max-w-4xl mx-auto bg-destructive/10 border-destructive/20">
        <CardHeader>
          <CardTitle className="text-destructive">Error Loading Provider</CardTitle>
        </CardHeader>
        <CardContent>
          <p>{error}</p>
          <Button 
            variant="outline" 
            onClick={() => navigate('/admin/providers')}
            className="mt-4"
          >
            Go Back
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="w-full max-w-4xl mx-auto">
      <CardHeader>
        <CardTitle className="text-2xl">Edit Provider</CardTitle>
        <CardDescription>Update the educational institution details</CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-6">
          <Tabs value={currentTab} onValueChange={setCurrentTab} className="w-full">
            <TabsList className="grid grid-cols-3 mb-6">
              <TabsTrigger value="basic">Basic Information</TabsTrigger>
              <TabsTrigger value="location">Location</TabsTrigger>
              <TabsTrigger value="additional">Additional Details</TabsTrigger>
            </TabsList>
            
            {/* Basic Information Tab */}
            <TabsContent value="basic" className="space-y-6">
              <div className="flex flex-col md:flex-row gap-6">
                {/* Left Column - Text Inputs */}
                <div className="space-y-4 flex-1">
                  <div>
                    <Label htmlFor="name" className="text-sm font-medium">
                      Provider Name <span className="text-red-500">*</span>
                    </Label>
                    <div className="mt-1">
                      <Input 
                        id="name" 
                        name="name" 
                        value={formData.name} 
                        onChange={handleInputChange} 
                        required 
                        maxLength={100}
                        className="w-full"
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        Maximum 100 characters ({formData.name.length}/100)
                      </p>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="type" className="text-sm font-medium">
                        Provider Type <span className="text-red-500">*</span>
                      </Label>
                      <Select 
                        value={formData.type} 
                        onValueChange={(value) => handleSelectChange('type', value)}
                      >
                        <SelectTrigger className="w-full mt-1">
                          <SelectValue placeholder="Select type" />
                        </SelectTrigger>
                       <SelectContent className="bg-white z-50">
                          <SelectItem value="university">University</SelectItem>
                          <SelectItem value="college">College</SelectItem>
                          <SelectItem value="high_school">High School</SelectItem>
                          <SelectItem value="elementary">Elementary School</SelectItem>
                          <SelectItem value="middle_school">Middle School</SelectItem>
                          <SelectItem value="vocational">Vocational School</SelectItem>
                          <SelectItem value="language_school">Language School</SelectItem>
                          <SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div>
                      <Label htmlFor="ownership" className="text-sm font-medium">
                        Ownership <span className="text-red-500">*</span>
                      </Label>
                      <Select 
                        value={formData.ownership} 
                        onValueChange={(value) => handleSelectChange('ownership', value)}
                      >
                        <SelectTrigger className="w-full mt-1">
                          <SelectValue placeholder="Select ownership" />
                        </SelectTrigger>
                       <SelectContent className="bg-white z-50">
                          <SelectItem value="public">Public</SelectItem>
                          <SelectItem value="private">Private</SelectItem>
                          <SelectItem value="charter">Charter</SelectItem>
                          <SelectItem value="international">International</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  
                  <div>
                    <Label htmlFor="yearFounded" className="text-sm font-medium">
                      Year Founded
                    </Label>
                    <Input 
                      id="yearFounded" 
                      name="yearFounded" 
                      type="number" 
                      value={formData.yearFounded} 
                      onChange={handleInputChange}
                      className="w-full mt-1" 
                      min="1400"
                      max={new Date().getFullYear()}
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="description" className="text-sm font-medium">
                      Description
                    </Label>
                    <Textarea 
                      id="description" 
                      name="description" 
                      value={formData.description} 
                      onChange={handleInputChange} 
                      rows={4}
                      className="w-full mt-1"
                      placeholder="Briefly describe the institution..."
                    />
                  </div>
                </div>
                
                {/* Right Column - Logo Upload */}
                <div className="w-full md:w-1/3 space-y-4">
                  <Label className="text-sm font-medium">Institution Logo</Label>
                  <div
                    className={`border-2 border-dashed rounded-lg p-4 flex flex-col items-center justify-center h-64 transition-colors ${
                      isDragging ? 'border-primary bg-primary/5' : 'border-gray-300'
                    }`}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                  >
                    {logoPreview ? (
                      <div className="relative w-32 h-32 mb-4">
                        <ProviderLogo 
                          filename={logoPreview} 
                          alt={formData.name || 'Provider logo'} 
                          className="w-32 h-32 object-cover rounded-md"
                        />
                        <button
                          type="button"
                          onClick={() => {
                            setLogo(null);
                            setLogoPreview(null);
                          }}
                          className="absolute top-0 right-0 bg-red-500 text-white rounded-full p-1"
                          aria-label="Remove logo"
                        >
                          <X size={16} />
                        </button>
                      </div>
                    ) : (
                      <>
                        <Upload className="h-10 w-10 text-gray-400 mb-2" />
                        <p className="text-sm text-center text-gray-500">
                          Drag & drop your logo here, or{' '}
                          <span 
                            className="text-primary cursor-pointer"
                            onClick={() => document.getElementById('logo')?.click()}
                          >
                            browse
                          </span>
                        </p>
                        <p className="text-xs text-gray-400 mt-1">
                          Recommended: Square image, 512x512px or larger
                        </p>
                        <Button 
                          type="button" 
                          variant="destructive" 
                          size="sm"
                          onClick={removeLogo}
                          className="mt-2"
                        >
                          Remove Logo
                        </Button>
                      </>
                    )}
                    <Input 
                      id="logo" 
                      name="logo" 
                      type="file" 
                      accept="image/*" 
                      onChange={handleLogoChange}
                      className="hidden"
                    />
                  </div>
                </div>
              </div>
              
              <div className="flex justify-between pt-4">
                <Button type="button" variant="outline" onClick={() => navigate('/admin/providers')}>
                  Cancel
                </Button>
                <Button type="button" onClick={() => setCurrentTab('location')}>
                  Next: Location
                </Button>
              </div>
            </TabsContent>
            
            {/* Location Tab */}
            <TabsContent value="location" className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="country" className="text-sm font-medium">
                    Country <span className="text-red-500">*</span>
                  </Label>
                  <div className="flex items-center mt-1">
                    <Globe className="h-4 w-4 text-gray-400 mr-2" />
                    <Select 
                      value={formData.country} 
                      onValueChange={(value) => handleSelectChange('country', value)}
                      disabled={true} // Disable the select to keep Tunisia as the only option
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Select country" />
                      </SelectTrigger>
                     <SelectContent className="bg-white z-50">
                        <SelectItem value="Tunisia">Tunisia</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                <div>
                  <Label htmlFor="state" className="text-sm font-medium">
                    State (Governorate) <span className="text-red-500">*</span>
                  </Label>
                  <div className="flex items-center mt-1">
                    <Building className="h-4 w-4 text-gray-400 mr-2" />
                    <Select 
                      value={formData.state} 
                      onValueChange={handleGovernorateChange}
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Select state" />
                      </SelectTrigger>
                     <SelectContent className="bg-white z-50">
                        {tunisianGovernorates.map(gov => (
                          <SelectItem key={gov} value={gov}>{gov}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                <div>
                  <Label htmlFor="city" className="text-sm font-medium">
                    City <span className="text-red-500">*</span>
                  </Label>
                  <div className="flex items-center mt-1">
                    <Building className="h-4 w-4 text-gray-400 mr-2" />
                    {formData.state ? (
                      <Select 
                        value={formData.city} 
                        onValueChange={(value) => handleSelectChange('city', value)}
                      >
                        <SelectTrigger className="w-full">
                          <SelectValue placeholder="Select city" />
                        </SelectTrigger>
                       <SelectContent className="bg-white z-50">
                          {governorateCities[formData.state]?.map(city => (
                            <SelectItem key={city} value={city}>{city}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <Input 
                        id="city" 
                        name="city" 
                        value={formData.city} 
                        onChange={handleInputChange} 
                        required 
                        className="w-full"
                        placeholder="Select a state first"
                        disabled={!formData.state}
                      />
                    )}
                  </div>
                </div>
                
                <div>
                  <Label htmlFor="postalCode" className="text-sm font-medium">
                    Postal Code
                  </Label>
                  <Input 
                    id="postalCode" 
                    name="postalCode" 
                    value={formData.postalCode} 
                    onChange={handleInputChange}
                    className="w-full mt-1" 
                  />
                </div>
                
                <div className="md:col-span-2">
                  <Label htmlFor="address" className="text-sm font-medium">
                    Address <span className="text-red-500">*</span>
                  </Label>
                  <Input 
                    id="address" 
                    name="address" 
                    value={formData.address} 
                    onChange={handleInputChange}
                    required
                    className="w-full mt-1"
                    placeholder="Street address"
                  />
                </div>
              </div>
              
              <div className="flex justify-between pt-4">
                <Button type="button" variant="outline" onClick={() => setCurrentTab('basic')}>
                  Back
                </Button>
                <Button type="button" onClick={() => setCurrentTab('additional')}>
                  Next: Additional Details
                </Button>
              </div>
            </TabsContent>
            
            {/* Additional Details Tab */}
            <TabsContent value="additional" className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="email" className="text-sm font-medium">
                    Email <span className="text-red-500">*</span>
                  </Label>
                  <div className="flex items-center mt-1">
                    <Mail className="h-4 w-4 text-gray-400 mr-2" />
                    <Input 
                      id="email" 
                      name="email" 
                      type="email" 
                      value={formData.email} 
                      onChange={handleInputChange} 
                      required 
                      className="w-full"
                    />
                  </div>
                </div>
                
                <div>
                  <Label htmlFor="phone" className="text-sm font-medium">
                    Phone
                  </Label>
                  <div className="flex items-center mt-1">
                    <Phone className="h-4 w-4 text-gray-400 mr-2" />
                    <Input 
                      id="phone" 
                      name="phone" 
                      value={formData.phone} 
                      onChange={handleInputChange}
                      className="w-full"
                      placeholder="+216 XX XXX XXX"
                    />
                  </div>
                </div>
                
                <div>
                  <Label htmlFor="website" className="text-sm font-medium">
                    Website
                  </Label>
                  <div className="flex items-center mt-1">
                    <Globe className="h-4 w-4 text-gray-400 mr-2" />
                    <Input 
                      id="website" 
                      name="website" 
                      value={formData.website} 
                      onChange={handleInputChange}
                      className="w-full"
                      placeholder="https://example.com"
                    />
                  </div>
                </div>
                
                <div>
                  <Label htmlFor="category" className="text-sm font-medium">
                    Category
                  </Label>
                  <Select 
                    value={formData.category} 
                    onValueChange={(value) => handleSelectChange('category', value)}
                  >
                    <SelectTrigger className="w-full mt-1">
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                   <SelectContent className="bg-white z-50">
                      {Object.values(categoryNames).map((category, index) => (
                         <SelectItem key={`${category}-${index}`} value={category.toString()}>{category}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label htmlFor="linkedin" className="text-sm font-medium">
                    LinkedIn
                  </Label>
                  <div className="flex items-center mt-1">
                    <Link2 className="h-4 w-4 text-gray-400 mr-2" />
                    <Input 
                      id="linkedin" 
                      name="linkedin" 
                      value={formData.linkedin} 
                      onChange={handleInputChange}
                      className="w-full"
                      placeholder="https://linkedin.com/company/example"
                    />
                  </div>
                </div>
                
                <div>
                  <Label htmlFor="x" className="text-sm font-medium">
                    X (Twitter)
                  </Label>
                  <div className="flex items-center mt-1">
                    <Link2 className="h-4 w-4 text-gray-400 mr-2" />
                    <Input 
                      id="x" 
                      name="x" 
                      value={formData.x} 
                      onChange={handleInputChange}
                      className="w-full"
                      placeholder="https://x.com/example"
                    />
                  </div>
                </div>
              </div>
              
              {/* Add the image gallery section here */}
              <div className="space-y-4">
                <Label className="text-sm font-medium">Additional Images</Label>
                
                {/* Display existing images */}
                {existingImages.length > 0 && (
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-4">
                    {existingImages.map((url, index) => (
                      <div key={`existing-${index}`} className="relative group">
                        <ProviderImage 
                          providerId={parseInt(id!, 10)}
                          filename={url} 
                          alt={`Provider image ${index + 1}`} 
                          className="w-full h-32 object-cover rounded-md"
                        />
                        <button
                          type="button"
                          onClick={() => removeExistingImage(url)}
                          className="absolute top-2 right-2 bg-red-500 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                
                {/* Display new image previews */}
                {imagesPreviews.length > 0 && (
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-4">
                    {imagesPreviews.map((preview, index) => (
                      <div key={`preview-${index}`} className="relative group">
                        <img 
                          src={preview} 
                          alt={`New image ${index + 1}`} 
                          className="w-full h-32 object-cover rounded-md"
                        />
                        <button
                          type="button"
                          onClick={() => removeImage(index)}
                          className="absolute top-2 right-2 bg-red-500 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                
                {/* Image upload button */}
                <label
                  htmlFor="images-upload"
                  className="w-full h-32 border-2 border-dashed border-gray-300 rounded-md flex items-center justify-center cursor-pointer hover:bg-gray-50"
                >
                  <div className="text-center">
                    <UploadIcon className="mx-auto h-8 w-8 text-gray-400" />
                    <span className="mt-2 block text-sm text-gray-600">Add Images</span>
                  </div>
                  <input
                    id="images-upload"
                    name="images"
                    type="file"
                    multiple
                    accept="image/*"
                    className="sr-only"
                    onChange={handleImagesChange}
                  />
                </label>
              </div>
              
              <div className="flex justify-between pt-4">
                <Button type="button" variant="outline" onClick={() => setCurrentTab('location')}>
                  Back
                </Button>
                <Button 
                  type="submit" 
                  disabled={storeLoading}
                  className="flex items-center gap-2"
                >
                  {storeLoading && <Loader2 className="h-4 w-4 animate-spin" />}
                  Update Provider
                </Button>
              </div>
            </TabsContent>
          </Tabs>
        </form>
      </CardContent>
    </Card>
  );
};

export default EditProviderForm;





  